// App configuration system to handle multiple applications and their specific features
import tatvaPediaLogo from "../assets/images/Tatvapedia_logo.svg";
import tatvaShotsLogo from "../assets/images/tatva-shorts.png";
import zymedPediaLogo from "../assets/images/zymedpedia.svg";
import zymedShotsLogo from "../assets/images/zymedshots.svg";
import tatvaAppLogo from "../assets/images/TatvaPracticecolor.png";
import zymedAppLogo from "../assets/images/zymedelite.svg";

const APP_TYPE_KEY = "app_type";

// Define types for app configuration
export interface AppBranding {
  appName: string;
  primaryColor: string;
  secondaryColor: string;
  showSponsorship: boolean; // Whether to show "Sponsored by" divs
  assets: {
    appLogo: string;
    pediaLogo: string;
    shotsLogo: string;
    // Add more assets as needed
  };
  labels: {
    pediaLabel: string;
    shotsLabel: string;
    aiLabel: string;
    appDescription: string;
    // Add more labels as needed
  };
}

export interface AppFeatures {
  // Feature flags can be added here
  enableFeatureX?: boolean;
  enableFeatureY?: boolean;
  // Add more feature flags as needed
}

export interface AppConfig {
  branding: AppBranding;
  features: AppFeatures;
}

// Define app configurations
const APP_CONFIGS: Record<string, AppConfig> = {
  // Default Tatva configuration
  tatva: {
    branding: {
      appName: "MedEco",
      primaryColor: "#4B4AD5", // Indigo color used in the app
      secondaryColor: "#3D8C40", // Green color used for selections
      showSponsorship: true, // Show "Sponsored by" divs for Tatva
      assets: {
        appLogo: tatvaAppLogo,
        pediaLogo: tatvaPediaLogo,
        shotsLogo: tatvaShotsLogo,
      },
      labels: {
        pediaLabel: "TatvaPedia",
        shotsLabel: "Tatva Shots",
        aiLabel: "TatvaAI",
        appDescription:
          "Tatvacare offers a whole host of services that will assist you in your Doctor journey.",
      },
    },
    features: {
      // Default feature flags
      enableFeatureX: false,
      enableFeatureY: false,
    },
  },

  // ZyMedElite configuration
  zymedelite: {
    branding: {
      appName: "ZyMedElite",
      primaryColor: "#4B4AD5", // Keeping the same for now, can be changed
      secondaryColor: "#3D8C40",
      showSponsorship: false, // Never show "Sponsored by" divs for ZyMedElite
      assets: {
        appLogo: zymedAppLogo,
        pediaLogo: zymedPediaLogo,
        shotsLogo: zymedShotsLogo,
      },
      labels: {
        pediaLabel: "ZymedPedia",
        shotsLabel: "ZymedShots",
        aiLabel: "ZymedAI",
        appDescription:
          "ZyMedElite offers a whole host of services that will assist you in your Doctor journey.",
      },
    },
    features: {
      // ZyMedElite-specific feature flags
      enableFeatureX: false,
      enableFeatureY: false,
    },
  },
};

/**
 * Initializes app type detection on app load.
 * Call this early in app initialization to ensure consistent configuration.
 */
export const initAppConfig = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const app = urlParams.get("app");

  if (app && APP_CONFIGS[app.toLowerCase()]) {
    localStorage.setItem(APP_TYPE_KEY, app.toLowerCase());
  }
};

/**
 * Returns the current app type (e.g., "tatva", "zymedelite")
 */
export const getAppType = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  const appFromUrl = urlParams.get("app");

  if (appFromUrl && APP_CONFIGS[appFromUrl.toLowerCase()]) {
    localStorage.setItem(APP_TYPE_KEY, appFromUrl.toLowerCase());
  }

  const storedApp = localStorage.getItem(APP_TYPE_KEY);
  return storedApp || "tatva"; // Default to "tatva" if not set
};

/**
 * Returns the complete app configuration
 */
export const getAppConfig = (): AppConfig => {
  const appType = getAppType();
  return APP_CONFIGS[appType] || APP_CONFIGS.tatva;
};

/**
 * Returns app branding configuration
 */
export const getAppBranding = (): AppBranding => {
  return getAppConfig().branding;
};

/**
 * Returns specific assets based on the current app type
 */
export const getAsset = (assetType: "app" | "pedia" | "shots"): string => {
  const { branding } = getAppConfig();
  if (assetType === "app") {
    return branding.assets.appLogo;
  } else if (assetType === "pedia") {
    return branding.assets.pediaLogo;
  } else if (assetType === "shots") {
    return branding.assets.shotsLogo;
  }
  return "";
};

/**
 * Returns specific label based on the current app type
 */
export const getLabel = (labelType: keyof AppBranding["labels"]): string => {
  const { branding } = getAppConfig();
  return branding.labels[labelType];
};

// Export legacy functions for backward compatibility
export const getConditionalAsset = getAsset;

/**
 * Returns whether sponsorship information should be shown
 * ZyMedElite users should never see "Sponsored by" divs
 */
export const shouldShowSponsorship = (): boolean => {
  const { branding } = getAppConfig();
  return branding.showSponsorship;
};

export const isZymedApp = (): boolean => {
  const { branding } = getAppConfig();
  return branding.appName === "ZyMedElite";
};

export const getAppName = (): string => {
  const { branding } = getAppConfig();
  return branding.appName;
};
